import { formatListSchema, inferMaybeWithToken } from './utils';

import { imageSizeSchema, jFileLittleSchema } from './file';

import { audienceAutocompleteSchema, jObjectAutocompleteSchema } from './autocomplete';
import { articleCreationSchema, jArticleListSchema, jArticleLittleSchema, jArticleViewSchema } from './article';
import { z, ZodType } from 'zod';

export const faqListSchema = jArticleListSchema.extend({
    faqColor: z.string().optional(),
    image: imageSizeSchema.optional(),
    type: z.literal('faq'),
});
export type FaqList = z.infer<typeof faqListSchema>;
export const faqViewSchema = jArticleViewSchema.and(faqListSchema).and(
    z.object({
        _audience: audienceAutocompleteSchema.array(),
        tags: jObjectAutocompleteSchema.array().optional(),
        isWeb: z.boolean(),
        faqContent: z
            .array(
                z.object({
                    title: z.string(),
                    id: z.string().optional(),
                    items: z.array(z.string()),
                }),
            )
            .optional(),
    }),
);
export type FaqView = z.infer<typeof faqViewSchema>;

export const inferExtendedFaqCategory = <T extends ZodType<FaqList>>(type: T) => {
    return type.and(
        z.object({
            _extend: z.object({
                itemsCount: z.number(),
            }),
        }),
    );
};
export type ExtendedFaqCategory<T extends FaqList> = z.infer<ReturnType<typeof inferExtendedFaqCategory<ZodType<T>>>>;

export const faqItemListSchema = jArticleListSchema.extend({
    type: z.literal('faqItem'),
    hierarchy: z.object({
        category: z.string().optional(),
        theme: z.string().optional(),
    }),
});
export type FaqItemList = z.infer<typeof faqItemListSchema>;

export const faqItemViewSchema = faqItemListSchema.omit({ hierarchy: true }).extend({
    faqResponse: z.string(),
    tags: jObjectAutocompleteSchema.array().optional(),
    uriCategory: jArticleLittleSchema.array(),
    themeId: z.string(),
    _nbAttachedFiles: z.number().optional(),
    _attachedFiles: jFileLittleSchema.array(),
    uiObjectLink: z.unknown(),
});
export type FaqItemView = z.infer<typeof faqItemViewSchema>;

export const createFaqCategorySchema = inferMaybeWithToken(articleCreationSchema.omit({ idSpot: true })).and(
    z.object({
        image: imageSizeSchema.nullable().optional(),
        tags: z.string().optional(),
        faqColor: z.string().optional(),
        isWeb: z.boolean().optional(),
        type: z.literal('faq'),
    }),
);
export type CreateFaqCategory = z.infer<typeof createFaqCategorySchema>;

export const editFaqCategorySchema = createFaqCategorySchema.and(
    z.object({
        uri: z.string(),
        format: formatListSchema.optional(),
    }),
);
export type EditFaqCategory = z.infer<typeof editFaqCategorySchema>;

export const faqTaxonomySchema = z.object({
    title: z.string(),
    id: z.string(),
});
export type FaqTaxonomy = z.infer<typeof faqTaxonomySchema>;

export const faqHookPropertiesSchema = z.object({
    _web: z.union([z.literal('0'), z.literal('1'), z.literal('')]).optional(),
    appImageText: z.string().optional(),
    appImage: z
        .union([
            z.string(),
            z.object({
                image: z.object({
                    id: z.string(),
                    type: z.string(),
                }),
            }),
        ])
        .optional(),
    _displayComment: z.union([z.literal('0'), z.literal('1'), z.literal('')]).optional(),
});
export type FaqHookProperties = z.infer<typeof faqHookPropertiesSchema>;

export const faqAccessSchema = z.object({
    createCategory: z.boolean(),
});
export type FaqAccess = z.infer<typeof faqAccessSchema>;

export const createFaqThemeSchema = z.object({
    idCategory: z.number(),
    title: z.string(),
});
export type CreateFaqTheme = z.infer<typeof createFaqThemeSchema>;

export const deleteFaqThemeSchema = z.object({
    idCategory: z.number(),
    indexTheme: z.number(),
    deleteEntries: z.boolean().optional(),
});
export type DeleteFaqTheme = z.infer<typeof deleteFaqThemeSchema>;

export const editFaqThemeSchema = z.object({
    idCategory: z.number(),
    indexTheme: z.number(),
    title: z.string(),
});
export type EditFaqTheme = z.infer<typeof editFaqThemeSchema>;

export const createFaqEntrySchema = inferMaybeWithToken(articleCreationSchema).and(
    z.object({
        tags: z.string().optional(),
        faqResponse: z.string().optional(),
        type: z.literal('faqItem'),
    }),
);
export type CreateFaqEntry = z.infer<typeof createFaqEntrySchema>;

export const addFaqEntrySchema = z.object({
    idEntry: z.number(),
    idCategory: z.number(),
    theme: z.number(),
});
export type AddFaqEntry = z.infer<typeof addFaqEntrySchema>;

export const editFaqEntrySchema = createFaqEntrySchema.and(
    z.object({
        uri: z.string(),
    }),
);
export type EditFaqEntry = z.infer<typeof editFaqEntrySchema>;

export const moveFaqEntrySchema = z.object({
    entryURI: z.string(),
    sourceURI: z.string().optional(),
    targetURI: z.string(),
    theme: z.number(),
});
export type MoveFaqEntry = z.infer<typeof moveFaqEntrySchema>;

export const moveFaqThemeSchema = z.object({
    sourceUri: z.string(),
    targetUri: z.string(),
    themeIndex: z.number(),
});
export type MoveFaqTheme = z.infer<typeof moveFaqThemeSchema>;
